<template>
  <b-modal
    ref="modalGruppeneinkaufBearbeiten"
    id="modalGruppeneinkaufBearbeiten"
    size="lg"
    :title="`Gruppeneinkauf der Reise ${reisekuerzel} bearbeiten`"
    @show="onShow"
    @hidden="onHidden"
    centered
    no-close-on-backdrop
    no-close-on-esc
  >
    <b-overlay :show="isLoading">
      <div v-for="(fh, idx) in gemeinsameFlughaefen" :key="fh">
        <hr v-if="idx > 0" class="my-4" />
        <div class="mb-2">
          <b-badge variant="light" class="badge-flughafen">{{ fh }}</b-badge>
        </div>

        <div>Felder zum Bearbeiten auswählen:</div>
        <div class="d-flex align-items-center mt-2" style="gap: 8px">
          <AppSelect
            v-model="proFlughafen[fh].fieldsToEdit"
            :options="fieldEditSelectOptions"
            label="Felder"
            placeholder="Feld auswählen..."
            search-placeholder="Feld suchen …"
            multiple
            clearable
            sort="alpha"
            class="w-100"
          />
        </div>

        <div class="mt-4 fields-table" v-if="proFlughafen[fh].fieldsToEdit.length > 0">
          <div
            v-for="item in gewaehlteFelder(proFlughafen[fh].fieldsToEdit)"
            :key="item.key"
            class="field-row"
          >
            <div class="field-label">{{ item.label }}</div>
            <div class="field-input">
              <b-input-group v-if="item.type === 'currency'">
                <b-form-input
                  size="sm"
                  v-model="proFlughafen[fh].form[item.key]"
                  type="number"
                  step="0.01"
                  placeholder="-"
                ></b-form-input>
                <b-input-group-append>
                  <b-input-group-text>€</b-input-group-text>
                </b-input-group-append>
              </b-input-group>

              <b-form-input
                v-else-if="item.type === 'number'"
                size="sm"
                v-model="proFlughafen[fh].form[item.key]"
                :number="true"
                type="number"
                min="0"
                step="1"
                placeholder="-"
              ></b-form-input>

              <b-form-input
                v-else
                size="sm"
                v-model="proFlughafen[fh].form[item.key]"
                placeholder="-"
              ></b-form-input>
            </div>
          </div>
        </div>
      </div>
    </b-overlay>

    <template #modal-footer="{ hide }">
      <b-button class="dialog-btn-secondary" @click="hide">Abbrechen</b-button>
      <b-button
        class="dialog-btn"
        @click="handleSubmit(hide)"
        :disabled="!isMindestensEinFeldBearbeitet"
        variant="primary"
      >
        Speichern
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import AppSelect from '@/components/common/app-select/app-select.vue';
import apiService from '@/core/common/services/api.service';

export default {
  components: {
    AppSelect,
  },
  props: {
    reisekuerzel: { type: String, default: '' },
    reiseterminIds: { type: Array, default: () => [] },
    gemeinsameFlughaefen: { type: Array, default: () => [] },
  },
  data() {
    return {
      isLoading: false,
      proFlughafen: {},
      editOptions: [
        { label: 'GRP PNR', key: 'gruppePnr', type: 'text' },
        { label: 'Eingekaufte Pax', key: 'eingekauftePax', type: 'number' },
        { label: 'Flugnr. GRP Hin', key: 'flugnummerGruppeHin', type: 'text' },
        { label: 'Flugnr. GRP Rück', key: 'flugnummerGruppeRueck', type: 'text' },
        { label: 'OB Class', key: 'obClass', type: 'text' },
        { label: 'IB Class', key: 'ibClass', type: 'text' },
        { label: 'Preis Netto', key: 'hauptstreckePreisNetto', type: 'currency' },
        { label: 'INFL Netto', key: 'inlandsflugPreis', type: 'currency' },
        { label: 'Taxes Gesamt', key: 'taxesFlug', type: 'currency' },
      ],
    };
  },
  computed: {
    // Das Select arbeitet mit den Feldschlüsseln, angezeigt wird die Beschriftung
    fieldEditSelectOptions() {
      return this.editOptions.map(option => ({ value: option.key, text: option.label }));
    },
    isMindestensEinFeldBearbeitet() {
      return this.gemeinsameFlughaefen.some(fh => {
        const entry = this.proFlughafen[fh];
        if (!entry) return false;
        return this.gewaehlteFelder(entry.fieldsToEdit).some(field => {
          const value = entry.form[field.key];
          return value !== null && value !== '';
        });
      });
    },
  },
  methods: {
    // Liefert die Feldkonfigurationen zu den gewählten Schlüsseln in der Reihenfolge der Auswahl
    gewaehlteFelder(keys) {
      return (keys ?? []).map(key => this.editOptions.find(option => option.key === key)).filter(Boolean);
    },
    // Erzeugt ein leeres Formular-Objekt mit allen bearbeitbaren Feldern auf null
    // Wird für jeden Flughafen einzeln aufgerufen, damit jeder seinen eigenen State hat
    createEmptyForm() {
      return {
        gruppePnr: null,
        flugnummerGruppeHin: null,
        flugnummerGruppeRueck: null,
        obClass: null,
        ibClass: null,
        eingekauftePax: null,
        hauptstreckePreisNetto: null,
        inlandsflugPreis: null,
        taxesFlug: null,
      };
    },
    // Baut das proFlughafen-Objekt auf, wenn das Modal geöffnet wird.
    initProFlughafen() {
      const obj = {};
      this.gemeinsameFlughaefen.forEach(fh => {
        obj[fh] = {
          fieldsToEdit: [],
          form: this.createEmptyForm(),
        };
      });
      this.proFlughafen = obj;
    },
    onShow() {
      this.initProFlughafen();
    },
    onHidden() {
      this.proFlughafen = {};
    },
    // Beim Speichern wird PRO Flughafen geprüft, ob Felder befüllt wurden
    async handleSubmit(hide) {
      this.isLoading = true;
      try {
        for (const fh of this.gemeinsameFlughaefen) {
          const entry = this.proFlughafen[fh];
          // Nur API-Call machen wenn mindestens ein Feld befüllt wurde
          const gewaehlt = this.gewaehlteFelder(entry.fieldsToEdit);
          const hatBefuellteFelder = gewaehlt.some(field => {
            const value = entry.form[field.key];
            return value !== null && value !== '';
          });
          if (!hatBefuellteFelder) continue;

          const gruppeneinkaufDto = { flughafenIataCode: fh };
          gewaehlt.forEach(field => {
            const value = entry.form[field.key];
            if (field.type === 'number' || field.type === 'currency') {
              gruppeneinkaufDto[field.key] = value != null && value !== '' ? Number(value) : null;
            } else {
              gruppeneinkaufDto[field.key] = value;
            }
          });

          console.log(gruppeneinkaufDto);

          await apiService.put('Reisetermin/bulkUpdateGruppeneinkauf', {
            reiseterminIds: this.reiseterminIds,
            gruppeneinkaufDto,
          });
        }

        this.$emit('gruppeneinkaufBearbeitet');
        hide();
      } catch (error) {
        console.error('Fehler beim Bearbeiten der Gruppeneinkäufe:', error);
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.fields-table {
  border-top: 1px solid var(--border);
}
.field-row {
  display: flex;
  align-items: center;
  border-bottom: 1px solid var(--border);
  padding: 8px 0;
}
.field-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}
.field-label {
  width: 160px;
  flex-shrink: 0;
  font-weight: 500;
  font-size: 14px;
  padding-left: 8px;
}
.field-input {
  flex: 1;
}
.field-input :deep(.input-group-text) {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}
.badge-flughafen {
  font-size: 14px;
  padding: 8px 20px;
  background-color: var(--primary);
  color: #ffffff;
  font-weight: 500;
  border-radius: 20px;
}
</style>
